import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/services/supabase';
import * as arcService from '@/services/arc';
import { getNtpNow, syncNtp } from '@/utils/ntp-sync';
import { useSettingsStore } from '@/stores/settings-store';
import { useArcStore } from '@/stores/arc-store';
import { cn } from '@/lib/utils';
import { AlertTriangle, Bell, Shield, ShieldAlert, Activity, CheckCircle, X, Zap, Filter, MoonStar, ArrowUp, ArrowDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import type { SiteSchedule } from '@asi/shared';

/** Extended event with site info (exported so parent can open the account drawer) */
export interface ArcEventWithSite {
  id: string;
  panel_id: string | null;
  site_id: string | null;
  cid_code: string;
  cid_qualifier: string;
  event_category: string;
  zone_no: number | null;
  zone_name: string | null;
  partition_no: number;
  user_code: string | null;
  severity: string;
  occurred_at: string;
  received_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  // Joined data
  site_name?: string;
  panel_serial?: string;
}

interface EventRibbonProps {
  className?: string;
  maxEvents?: number;
  autoScroll?: boolean;
  /** Called with full event object when operator clicks "Procesar" — opens the process modal */
  onProcess?: (event: ArcEventWithSite) => void;
  /** Called when operator clicks "Lote" in a client row — opens batch modal pre-filtered to that site */
  onBatchBySite?: (siteId: string, siteName: string, events: ArcEventWithSite[]) => void;
  /** Filters passed from parent page */
  severityFilter?: string;
  acknowledgedFilter?: string;
  search?: string;
  /** Increment to force a full reload of ribbon events from the DB (e.g. after a batch process) */
  refreshTrigger?: number;
  /** Called after a batch completes — removes matching events optimistically before the re-fetch */
  onBatchProcessed?: (siteId?: string | null, category?: string | null) => void;
  /** Emits the currently visible (tab + all filters applied) events so the parent
   * can drive a context-aware "Procesar lote" button and counter. */
  onFilteredChange?: (events: ArcEventWithSite[]) => void;
}

/** Get icon for event severity (matches alarm_event_severity enum) */
function getSeverityIcon(severity: string) {
  switch (severity) {
    case 'CRITICAL':
      return <ShieldAlert className="h-4 w-4 text-red-500" />;
    case 'ALARM':
      return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    case 'WARNING':
      return <Bell className="h-4 w-4 text-yellow-500" />;
    case 'INFO':
      return <Shield className="h-4 w-4 text-blue-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-500" />;
  }
}

/** Get row background color for severity */
function getSeverityRowClass(severity: string, acknowledged: boolean) {
  if (acknowledged) return 'bg-muted/30';
  switch (severity) {
    case 'CRITICAL':
      return 'bg-red-500/10';
    case 'ALARM':
      return 'bg-orange-500/10';
    case 'WARNING':
      return 'bg-yellow-500/10';
    case 'INFO':
      return 'bg-blue-500/10';
    default:
      return '';
  }
}

/** Format full datetime — always in Guatemala local time (UTC-6, no DST) */
function formatDateTime(date: string): { date: string; time: string } {
  const d = new Date(date);
  const TZ = 'America/Guatemala';
  return {
    date: d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TZ }),
    time: d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: TZ }),
  };
}

/**
 * Comprehensive Ademco Contact ID (CID) code descriptions.
 * Covers the full standard range (100-900) + common Hikvision extensions.
 */
const CID_DESCRIPTIONS: Record<string, string> = {
  // ── 100-199: Alarmas ──────────────────────────────────────
  '100': 'Emergencia Médica',
  '101': 'Emergencia Personal',
  '102': 'Sin Reporte',
  '103': 'Alarma de Zona',
  '104': 'Alarma de Punto',
  '105': 'Alarma Supervisada',
  '106': 'Alarma Silenciosa',
  '107': 'Alarma Local',
  '108': 'Monitoreo de Punto',
  '110': 'Alarma de Fuego',
  '111': 'Humo Detectado',
  '112': 'Combustión Detectada',
  '113': 'Agua Detectada',
  '114': 'Calor Detectado',
  '115': 'Inundación',
  '116': 'CO Detectado',
  '117': 'Gas Detectado',
  '118': 'Alta Temperatura',
  '119': 'Explosión',
  '120': 'Pánico',
  '121': 'Coacción (Duress)',
  '122': 'Pánico Silencioso',
  '123': 'Pánico Audible',
  '124': 'Agresión',
  '125': 'Alarma Personal',
  '126': 'Código de Coacción',
  '127': 'Alarma Especial (Pánico App)',
  '128': 'Alarma de Agua',
  // NOTE (2026-09-11): Hikvision AX PRO reports CID 129 for the PHYSICAL PANIC
  // BUTTON zone type — confirmed across 30+ independent panels/sites, all
  // consistently sending 1129/3129 for their "Botón de pánico" zone. This is
  // NOT the generic Ademco "thermal" meaning; do not revert to "Alarma Térmica".
  '129': 'Pánico',
  '130': 'Robo',
  '131': 'Zona Perimetral',
  '132': 'Interior',
  '133': 'Entrada 24h',
  '134': 'Entrada/Salida',
  '135': 'Zona Día/Noche',
  '136': 'Zona Exterior',
  '137': 'Tamper',
  '138': 'Pre-Alarma',
  '139': 'Verificación de Intrusión',
  '140': 'Alarma General',
  '141': 'Falsa Alarma Confirmada',
  '142': 'Robo Silencioso',
  '143': 'Robo Audible',
  '145': 'Robo Forzado',
  '146': 'Alarma de Intrusión',
  '147': 'Alarma de Movimiento',
  '150': 'Fuga de Tubería',
  '151': 'Gas Detectado',
  '154': 'Nivel de Agua Alto',
  '155': 'Problema de Calefacción',
  '158': 'Temperatura Alta',
  '159': 'Temperatura Baja',
  '162': 'Monóxido de Carbono',
  // ── 200-299: Supervisión ──────────────────────────────────
  '200': 'Supervisión General',
  '201': 'Falla de Energía en Zona',
  '202': 'Falla de Batería en Zona',
  '203': 'Baja Carga de Batería',
  '204': 'Batería Faltante',
  '205': 'Falla Batería de Control',
  '206': 'Batería Débil',
  '207': 'Pérdida de Energía',
  '208': 'Oscilación de Energía',
  // ── 300-399: Fallas ───────────────────────────────────────
  '300': 'Problema del Sistema',
  '301': 'Falla Energía AC',
  '302': 'Batería Baja',
  '303': 'Error Checksum RAM',
  '304': 'Error Checksum ROM',
  '305': 'Reset del Sistema',
  '306': 'Programación Cambiada',
  '307': 'Falla Auto-prueba',
  '309': 'Falla Prueba Batería',
  '310': 'Falla a Tierra',
  '311': 'Batería Ausente',
  '318': 'Agotamiento de Energía',
  '312': 'Sobrecorriente Fuente',
  '313': 'Supervisión de Zona',
  '320': 'Falla Sirena/Relé',
  '321': 'Falla Sirena AUX',
  '330': 'Problema de Zona',
  '331': 'Loop Sondeo Abierto',
  '332': 'Loop Sondeo Corto',
  '333': 'Problema Expansor',
  '334': 'Falla Repetidor',
  '338': 'Pérdida Comunicación RF',
  '341': 'Sabotaje Módulo Expansor',
  '344': 'Interferencia RF',
  '345': 'Inhibidor de Señal RF',
  '350': 'Falla Comunicaciones',
  '351': 'Falla Línea Telefónica',
  '352': 'Pérdida Canal Telefónico',
  '353': 'Falla Comunicación Expansor',
  '354': 'Falla al Comunicar Evento',
  '356': 'Pérdida Sondeo Central',
  '357': 'Falla de Red',
  '358': 'Pérdida Red GPRS',
  '359': 'Pérdida Canal IP',
  '363': 'Problema de Temperatura',
  '370': 'Loop de Protección',
  '373': 'Tamper Zona Fuego',
  '374': 'Error de Salida',
  '380': 'Falla Sensor',
  '381': 'Pérdida Supervisión RF',
  '382': 'Pérdida RF Batería Baja',
  '383': 'Tamper Sensor',
  '384': 'Batería Baja Sensor',
  '389': 'Problema Detector',
  // ── 400-499: Armado/Desarmado ─────────────────────────────
  '400': 'Armado/Desarmado',
  '401': 'Armado Away',
  '402': 'Armado Away Parcial',
  '403': 'Desarmado',
  '404': 'Apertura Tardía',
  '405': 'Cierre Tardío',
  '406': 'Cancelar',
  '407': 'Acceso Remoto',
  '408': 'Armado Rápido',
  '409': 'Armado Keyswitch',
  '411': 'Solicitud Callback',
  '412': 'Acceso por Teléfono',
  '413': 'Acceso Fallido por Teléfono',
  '421': 'Acceso Denegado',
  '422': 'Código Erróneo',
  '423': 'Acceso Forzado',
  '426': 'Puerta Sostenida Abierta',
  '429': 'Puerta Abierta Mucho Tiempo',
  '431': 'Código Duplicado',
  '432': 'Código Expirado',
  '441': 'Armado Stay',
  '442': 'Armado Stay Parcial',
  '450': 'Apertura Tardía',
  '451': 'Sin Apertura',
  '452': 'Cierre Tardío',
  '454': 'Cierre de Excepción',
  '456': 'Armado Parcial',
  '457': 'Cierre de Guardia',
  '459': 'Cierre Reciente',
  '461': 'Código Incorrecto',
  '463': 'Re-armado Post-alarma',
  '464': 'Armado con Fallas',
  '465': 'Auto-armado',
  '466': 'Código Erróneo en Teclado',
  '471': 'Código de Coacción',
  '472': 'Código de Supervisor',
  '480': 'Actividad de Asistente',
  '482': 'Apertura de Grupo',
  '483': 'Cierre de Grupo',
  '489': 'Cancelar por Usuario',
  '490': 'Armado Anticipado',
  '491': 'Armado Fuera de Horario',
  '492': 'Cierre Forzado',
  // ── 500-599: Bypass / Cambios de red (verificado contra raw_message real) ──
  '501': 'Lector Deshabilitado',
  '502': 'Acceso por Llave',
  '556': 'Cambio de Red',
  '570': 'Bypass de Zona',
  '571': 'Unbypass de Zona',
  '572': 'Bypass Zona 24h',
  '573': 'Bypass Robo',
  '574': 'Bypass de Grupo',
  '575': 'Bypass de Punto',
  '577': 'Bypass Habilitado',
  '578': 'Bypass Deshabilitado',
  // ── 600-699: Pruebas y Especiales ────────────────────────
  '601': 'Test Manual',
  '602': 'Test Periódico',
  '603': 'Test Periódico Recibido',
  '604': 'Prueba de Incendio',
  '606': 'Test de Comunicación',
  '607': 'Walk Test Iniciado',
  '608': 'Walk Test Finalizado',
  '609': 'Test de Batería',
  '611': 'Punto Probado OK',
  '612': 'Punto Sin Probar',
  '614': 'Notificación Fecha/Hora',
  '616': 'Solicitud de Servicio',
  '621': 'Log Reiniciado',
  '622': 'Log de Eventos Lleno',
  '624': 'Notificación de Contador',
  '625': 'Fecha/Hora Reiniciada',
  '627': 'Excepción de Hora',
  '628': 'Bypass de Activación',
  '629': 'Prueba de Alarma',
  '630': 'Reset de Alarma',
  '642': 'Programado por Instalador',
  '643': 'Programado por Usuario',
  '644': 'Programación Cambiada',
  // ── 700-799: Actividad de Usuario ────────────────────────
  '700': 'Actividad de Usuario',
  '701': 'Dispositivo Activado',
  '703': 'Reset de Código',
  '720': 'Prueba Telefónica Remota',
  '730': 'Control Salida 1',
  '731': 'Control Salida 2',
  '732': 'Control Salida 3',
  '740': 'Bloqueo de Acceso',
  '741': 'Desbloqueo de Acceso',
  '742': 'Bloqueo Forzado',
  '743': 'Desbloqueo Forzado',
  // ── 800-899: Eventos de Sistema ──────────────────────────
  '800': 'Actividad de Control',
  '801': 'Secuencia Especial',
  '804': 'Alarma Sin Responder',
  '806': 'Mensaje de Control de Acceso',
  '810': 'Emergencia desde App (SOS)',
  '811': 'Incendio desde App',
  '813': 'Disparo de Temporizador',
  '815': 'Alarma Reconocida',
  '816': 'Evento de Usuario',
  '817': 'Mensaje del Sistema',
  '818': 'Modo de Emergencia',
  '820': 'Test de Batería Remoto',
  '821': 'Acceso Central',
  '822': 'Fallo de Armado',
  '823': 'Login Remoto',
  '824': 'Salida Remota',
  '825': 'Activación de Zona',
  '826': 'Reporte de Evento',
  '862': 'Teclado Bloqueado',
  '865': 'Tag/Tarjeta No Registrada',
  // ── 900-999: Misceláneos ──────────────────────────────────
  '900': 'Problema Sin Clasificar',
  '901': 'Actualización de Firmware',
  '902': 'Actualización de Base de Datos',
  // Hikvision AX PRO 9xx: communication / device troubles (verified 2026-10-22 against the panels'
  // own description field) — NOT arm/disarm methods as previously assumed.
  '914': 'Detector inalámbrico desconectado', '915': 'Batería baja de detector',
  '916': 'Módulo de expansión o teclado desconectado', '920': 'Datos celulares desconectados',
  '921': 'Falla de tarjeta SIM', '922': 'Falla de Wi-Fi', '923': 'Falla de radio inalámbrica (RF)',
  '925': 'Batería baja de control remoto', '930': 'Conflicto de dirección IP', '931': 'Red desconectada',
  '975': 'Detector agregado/eliminado', '977': 'Módulo agregado/eliminado', '979': 'Dispositivo inalámbrico agregado/eliminado',
  '988': 'Inactivación permanente externa', '989': 'Alarma médica silenciosa',
  '455': 'Auto-armado fallido', '467': 'Código incorrecto en teclado', '847': 'Emergencia médica desde app',
  // ── Hikvision HPP extendidos (4 dígitos directos) ────────
  '1601': 'Test Manual',
  '1602': 'Test Periódico',
  '1603': 'Test de Zona',
  '3601': 'Restauración Test Manual',
  '3602': 'Restauración Test Periódico',
};

function getCidDescription(code: string): string {
  // Handle 4-digit HPP codes (QCCC format): strip qualifier prefix (1xxx/3xxx)
  const shortCode = code.length === 4 && /^[13]\d{3}$/.test(code) ? code.slice(1) : code;

  if (CID_DESCRIPTIONS[shortCode]) return CID_DESCRIPTIONS[shortCode];
  if (CID_DESCRIPTIONS[code]) return CID_DESCRIPTIONS[code];

  // Smart fallback by code range — no event ever shows "Evento CID XXXX"
  const num = parseInt(shortCode, 10);
  if (num >= 100 && num <= 109) return 'Emergencia';
  if (num >= 110 && num <= 119) return 'Alarma de Fuego';
  if (num >= 120 && num <= 129) return 'Alarma de Pánico';
  if (num >= 130 && num <= 159) return 'Alarma de Robo';
  if (num >= 160 && num <= 199) return 'Alarma de Gas/CO';
  if (num >= 200 && num <= 299) return 'Supervisión';
  if (num >= 300 && num <= 399) return 'Falla del Sistema';
  if (num >= 400 && num <= 499) return 'Apertura/Cierre';
  if (num >= 500 && num <= 599) return 'Acceso / Bypass';
  if (num >= 600 && num <= 699) return 'Prueba / Especial';
  if (num >= 700 && num <= 799) return 'Actividad de Usuario';
  if (num >= 800 && num <= 899) return 'Evento de Sistema';
  if (num >= 900 && num <= 999) return 'Armado/Desarmado (Hikvision)';
  return `Código ${code}`;
}

/** Category labels */
const CATEGORY_LABELS: Record<string, string> = {
  alarm: 'Alarma',
  restore: 'Restauración',
  open_close: 'Apertura/Cierre',
  trouble: 'Falla',
  test: 'Prueba',
  system: 'Sistema',
  bypass: 'Bypass',
};

/**
 * For open_close events, determine whether it's apertura (disarm/open) or cierre (arm/close).
 * In CID protocol: 1xxx prefix = Event (arming = closing premises),
 *                  3xxx prefix = Restore (disarming = opening premises).
 */
function getOpenCloseType(event: ArcEventWithSite): 'Apertura' | 'Cierre' | null {
  if (event.event_category !== 'open_close') return null;
  const code = event.cid_code;
  const q = event.cid_qualifier;
  // Hikvision AX PRO convention (opposite of standard Contact ID):
  //   3xxx / R = restored to armed/secure state = Cierre (armed)
  //   1xxx / E = new access event = Apertura (disarmed)
  if (/^3\d{3}$/.test(code) || q === 'R') return 'Cierre';
  if (/^1\d{3}$/.test(code) || q === 'E') return 'Apertura';
  return null;
}

function getCategoryLabel(event: ArcEventWithSite): string {
  return getOpenCloseType(event) ?? CATEGORY_LABELS[event.event_category] ?? event.event_category;
}

/**
 * Display name for the Evento column.
 * open_close → "Apertura" / "Cierre" (with CID description as subtitle in JSX — handled inline)
 * everything else → standard CID description
 */
function getEventDisplayName(event: ArcEventWithSite): string {
  return getOpenCloseType(event) ?? getCidDescription(event.cid_code);
}

/** Sort events newest-first, stable (preserves relative order for equal timestamps).
 *  Used only by the in-memory cache merge to keep the most recent N events when capping
 *  to maxEvents. The displayed order is controlled by sortEvents() + user column choice. */
function sortByTime(arr: ArcEventWithSite[]): ArcEventWithSite[] {
  return [...arr].sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime());
}

/** Sortable columns shown in the cinta header */
type SortField = 'received_at' | 'event' | 'site_name' | 'event_category' | 'zone_no' | 'severity' | 'priority';
type SortDir = 'asc' | 'desc';

const SEVERITY_ORDER: Record<string, number> = { CRITICAL: 4, ALARM: 3, WARNING: 2, INFO: 1 };

/** Stable sort by the chosen column. Tie-breaker is always received_at ASC so FIFO
 *  order is preserved within equal values (e.g. multiple events from the same site). */
function sortEvents(arr: ArcEventWithSite[], field: SortField, dir: SortDir): ArcEventWithSite[] {
  const m = dir === 'asc' ? 1 : -1;
  return [...arr].sort((a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (field) {
      case 'received_at':
        av = new Date(a.received_at).getTime();
        bv = new Date(b.received_at).getTime();
        break;
      case 'site_name':
        av = (a.site_name ?? '').toLowerCase();
        bv = (b.site_name ?? '').toLowerCase();
        break;
      case 'event_category':
        av = a.event_category;
        bv = b.event_category;
        break;
      case 'zone_no':
        av = a.zone_no ?? -1;
        bv = b.zone_no ?? -1;
        break;
      case 'severity':
        av = SEVERITY_ORDER[a.severity] ?? 0;
        bv = SEVERITY_ORDER[b.severity] ?? 0;
        break;
      case 'priority':
        av = arcService.getEventPriority(a);
        bv = arcService.getEventPriority(b);
        break;
      case 'event':
      default:
        av = a.cid_code;
        bv = b.cid_code;
        break;
    }
    if (av < bv) return -1 * m;
    if (av > bv) return 1 * m;
    // FIFO tie-breaker: older first
    return new Date(a.received_at).getTime() - new Date(b.received_at).getTime();
  });
}

/** Categories for each tab */
const ALARM_CATEGORIES = ['alarm', 'restore', 'trouble', 'open_close', 'bypass'];
const SYSTEM_CATEGORIES = ['test', 'system'];

type TabFilter = 'alarmas' | 'sistema' | 'tormentas';

// Module-level caches for site/panel name lookups.
// Reused across events from the same panel so flooding (e.g. repeat arming events)
// does not fire redundant Supabase HTTP requests and exhaust browser connections.
const siteNameCache = new Map<string, string>();
const panelSerialCache = new Map<string, string>();
// In-flight sets: prevent duplicate concurrent queries when multiple events sharing
// the same site_id / panel_id arrive before the first lookup resolves and populates
// the cache (common during reconcile of 5000+ pending events without site_name).
const siteNameInFlight = new Set<string>();
const panelSerialInFlight = new Set<string>();
// NOTE: Schedule cache is now per-component-instance (useRef), not module-level,
// to prevent cross-effect race conditions on mount. See component body.

// ─── Panel Storm Detection ────────────────────────────────────────────

/** Rolling window for storm detection (5 minutes) */
const STORM_WINDOW_MS = 5 * 60 * 1000;
/** ≥5 events from same panel in window → flood warning */
const FLOOD_THRESHOLD = 5;
/** ≥10 events from same panel in window → storm alert */
const STORM_THRESHOLD = 10;

interface PanelStorm {
  key: string;                    // site_id or panel_id (grouping key)
  siteId: string | null;          // actual site_id for filtering
  siteName: string;
  panelSerial: string;
  recentEvents: ArcEventWithSite[];
  dominantCode: string;
  dominantCodeCount: number;
  diagnosis: string;
  level: 'flood' | 'storm';
}

function computeStorms(events: ArcEventWithSite[]): PanelStorm[] {
  const now = getNtpNow();
  const grouped = new Map<string, ArcEventWithSite[]>();
  for (const e of events) {
    // Only count unacknowledged events — banner must clear after an operator processes the batch
    if (e.acknowledged_at) continue;
    if (now - new Date(e.received_at).getTime() > STORM_WINDOW_MS) continue;
    const key = e.site_id ?? e.panel_id ?? 'unknown';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(e);
  }
  const storms: PanelStorm[] = [];
  for (const [key, recent] of grouped) {
    if (recent.length < FLOOD_THRESHOLD) continue;
    const codeCounts = new Map<string, number>();
    for (const e of recent) codeCounts.set(e.cid_code, (codeCounts.get(e.cid_code) ?? 0) + 1);
    const [dominantCode, dominantCodeCount] = [...codeCounts.entries()].sort((a, b) => b[1] - a[1])[0];
    const sample = recent[0];
    storms.push({
      key,
      siteId: sample.site_id,
      siteName: sample.site_name ?? 'Sin asignar',
      panelSerial: sample.panel_serial ?? key,
      recentEvents: recent,
      dominantCode,
      dominantCodeCount,
      diagnosis: getStormDiagnosis(dominantCode, recent.length),
      level: recent.length >= STORM_THRESHOLD ? 'storm' : 'flood',
    });
  }
  return storms.sort((a, b) => b.recentEvents.length - a.recentEvents.length);
}

function getStormDiagnosis(code: string, count: number): string {
  const short = code.length === 4 && /^[13]\d{3}$/.test(code) ? code.slice(1) : code;
  const num = parseInt(short, 10);
  if (num === 601 || num === 602)
    return 'Intervalo de test muy corto o panel en bucle. Revisar configuración de reporte periódico en el panel.';
  if ((num >= 401 && num <= 409) || (num >= 441 && num <= 442))
    return 'Armado/desarmado repetido. Posible zona abierta, puerta sin cerrar correctamente, o usuario en bucle.';
  if (num >= 110 && num <= 119)
    return 'Alarmas de incendio repetidas. Verificar detector de humo/calor. Posible sensor defectuoso o activo.';
  if (num >= 130 && num <= 149)
    return 'Múltiples alarmas de intrusión. Posible sensor o zona defectuosa. Verificar si hay alarma real activa.';
  if (num === 301 || num === 302 || num === 311)
    return 'Falla de energía/batería repetida. Revisar alimentación AC y batería de respaldo del panel.';
  if (num >= 300 && num <= 399)
    return 'Fallas del sistema repetidas. Posible tamper, cableado defectuoso o falla de hardware en el panel.';
  return `${count} eventos en 5 minutos del mismo panel. Posible bucle de comunicación, zona inestable o falla de hardware. Revisar el panel en sitio.`;
}

function PanelStormBanner({
  storm,
  onDismiss,
  onFilter,
}: {
  storm: PanelStorm;
  onDismiss: () => void;
  onFilter: () => void;
}) {
  const isStorm = storm.level === 'storm';
  return (
    <div className={cn(
      'flex items-start gap-3 px-4 py-3 rounded-lg border mb-2 flex-shrink-0',
      isStorm ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30',
    )}>
      <Zap className={cn('h-4 w-4 mt-0.5 flex-shrink-0 animate-pulse', isStorm ? 'text-red-500' : 'text-amber-500')} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('text-xs font-bold uppercase tracking-wide', isStorm ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400')}>
            {isStorm ? '⚡ Tormenta de eventos' : '⚠ Actividad elevada'}
          </span>
          <span className="font-semibold text-sm text-foreground">{storm.siteName}</span>
          <span className="font-mono text-xs text-muted-foreground">[{storm.panelSerial}]</span>
          <span className={cn('text-xs font-bold', isStorm ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400')}>
            {storm.recentEvents.length} eventos / 5 min
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Código dominante:{' '}
          <span className="font-mono font-semibold text-foreground">{storm.dominantCode}</span>
          {' '}({getCidDescription(storm.dominantCode)}) ×{storm.dominantCodeCount}
          {' — '}{storm.diagnosis}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onFilter}
          title="Filtrar cinta a este cliente"
          className="px-2 py-1 text-xs rounded border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors whitespace-nowrap"
        >
          Ver cinta
        </button>
        <button
          onClick={onDismiss}
          title="Descartar (reaparece si sigue escalando)"
          className="p-1 rounded hover:bg-muted transition-colors ml-1"
        >
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}

export function EventRibbon({
  className,
  maxEvents = 50,
  autoScroll = true,
  onProcess,
  onBatchBySite,
  severityFilter = 'ALL',
  acknowledgedFilter = 'ALL',
  search = '',
  refreshTrigger = 0,
  onFilteredChange,
}: EventRibbonProps) {
  const [events, setEvents] = useState<ArcEventWithSite[]>([]);

  // Exposed via ref so the parent can trigger optimistic removal after a batch
  const removeProcessed = (siteId?: string | null, category?: string | null) => {
    setEvents(prev => prev.filter(e => {
      if (e.acknowledged_at) return true; // already processed, keep
      if (siteId && e.site_id !== siteId) return true; // different site, keep
      if (category && category !== 'ALL' && e.event_category !== category) return true; // different category, keep
      return false; // matches filter — remove optimistically
    }));
  };

  // Expose removeProcessed through the onBatchProcessed prop pattern
  useEffect(() => {
    // nothing — kept for future imperative ref pattern if needed
  }, []);
  const [activeTab, setActiveTab] = useState<TabFilter>('alarmas');
  const [isLive, setIsLive] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [siteFilter, setSiteFilter] = useState<{ siteId: string; siteName: string } | null>(null);
  const [activeStorms, setActiveStorms] = useState<PanelStorm[]>([]);
  const dismissedStormsRef = useRef<Map<string, number>>(new Map());

  // Column sort — priority default so the most critical events surface first.
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Pagination — operators expect a manageable view; large pages slow down
  // rendering and make it easy to lose track of the queue head.
  const [pageSize, setPageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(0);
  // Per-instance schedule cache (useRef → resets on mount, no inter-effect race conditions)
  const scheduleCache = useRef(new Map<string, SiteSchedule>());
  const scheduleFetching = useRef(new Set<string>());
  // Incremented every time a site schedule loads so out-of-hours badges re-render
  const [scheduleVersion, setScheduleVersion] = useState(0);

  // Boot NTP sync whenever settings change
  const ntpSettings = useSettingsStore(s => s.ntp);
  useEffect(() => { syncNtp(ntpSettings); }, [ntpSettings]);

  // Sync from the arc-store:
  //  1. NEW events arriving via WebSocket that aren't in the ribbon yet → prepend them
  //  2. Status changes (acknowledged_at / resolved_at) on existing events → patch in place
  const storeEvents = useArcStore((s) => s.events);
  useEffect(() => {
    if (storeEvents.length === 0) return;

    setEvents((prev) => {
      const prevIds = new Set(prev.map((e) => e.id));

      // Split store events into "incoming" (new) vs known
      // Only add incoming events that pass the current acknowledgedFilter — prevents
      // a flash of stale events that would be immediately hidden once patched.
      const incoming = storeEvents.filter((e) => {
        if (prevIds.has(e.id)) return false;
        if (acknowledgedFilter === 'PENDING' && e.acknowledged_at) return false;
        if (acknowledgedFilter === 'ACKNOWLEDGED' && !e.acknowledged_at) return false;
        return true;
      });

      // Patch acknowledged_at / resolved_at on existing ribbon rows
      const updated = prev.map((ribbonEvent) => {
        const storeEvent = storeEvents.find((e) => e.id === ribbonEvent.id);
        if (!storeEvent) return ribbonEvent;
        if (
          storeEvent.acknowledged_at !== ribbonEvent.acknowledged_at ||
          storeEvent.resolved_at !== ribbonEvent.resolved_at
        ) {
          return {
            ...ribbonEvent,
            acknowledged_at: storeEvent.acknowledged_at,
            resolved_at: storeEvent.resolved_at,
          };
        }
        return ribbonEvent;
      });

      if (incoming.length === 0) return updated;

      // Cast incoming store events to ArcEventWithSite — site_name will be enriched async below
      const newRows = incoming as unknown as ArcEventWithSite[];
      // Sort after merge so the list is always newest-first regardless of arrival source
      return sortByTime([...newRows, ...updated]).slice(0, maxEvents);
    });

    // Async-enrich site_name / panel_serial for events that came in without them
    // (WebSocket events don't carry JOIN fields)
    storeEvents.forEach(async (storeEvent) => {
      if ((storeEvent as ArcEventWithSite).site_name) return; // already enriched
      if (!storeEvent.site_id && !storeEvent.panel_id) return;

      let siteName: string | undefined;
      let panelSerial: string | undefined;

      if (storeEvent.site_id) {
        if (siteNameCache.has(storeEvent.site_id)) {
          siteName = siteNameCache.get(storeEvent.site_id);
        } else if (!siteNameInFlight.has(storeEvent.site_id)) {
          siteNameInFlight.add(storeEvent.site_id);
          const { data } = await supabase.from('alarm_sites').select('name').eq('id', storeEvent.site_id).single();
          siteNameInFlight.delete(storeEvent.site_id);
          if (data?.name) { siteNameCache.set(storeEvent.site_id, data.name); siteName = data.name; }
        } else {
          // Another enrichment is already fetching this site — skip to avoid duplicate queries.
          return;
        }
        // Pre-load schedule for this site so out-of-hours badges render without an extra click
        if (!scheduleCache.current.has(storeEvent.site_id) && !scheduleFetching.current.has(storeEvent.site_id)) {
          scheduleFetching.current.add(storeEvent.site_id);
          arcService.fetchSiteSchedule(storeEvent.site_id).then(s => {
            scheduleCache.current.set(storeEvent.site_id!, s);
            setScheduleVersion(v => v + 1);
          }).catch(() => { /* non-critical */ })
            .finally(() => { scheduleFetching.current.delete(storeEvent.site_id!); });
        }
      }

      if (storeEvent.panel_id) {
        if (panelSerialCache.has(storeEvent.panel_id)) {
          panelSerial = panelSerialCache.get(storeEvent.panel_id);
        } else if (!panelSerialInFlight.has(storeEvent.panel_id)) {
          panelSerialInFlight.add(storeEvent.panel_id);
          const { data } = await supabase.from('alarm_panels').select('serial_number').eq('id', storeEvent.panel_id).single();
          panelSerialInFlight.delete(storeEvent.panel_id);
          if (data?.serial_number) { panelSerialCache.set(storeEvent.panel_id, data.serial_number); panelSerial = data.serial_number; }
        }
      }

      if (!siteName && !panelSerial) return;

      setEvents((prev) =>
        prev.map((e) =>
          e.id === storeEvent.id
            ? { ...e, site_name: e.site_name ?? siteName, panel_serial: e.panel_serial ?? panelSerial }
            : e
        )
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeEvents]);

  // Recompute panel storms on every events update and every 30s (so aged-out events clear the banner)
  useEffect(() => {
    function recompute() {
      const storms = computeStorms(events);
      const visible = storms.filter(s => {
        const dismissedAt = dismissedStormsRef.current.get(s.key);
        // Re-alert if the panel sent 5+ new events since the operator dismissed it
        return dismissedAt === undefined || s.recentEvents.length >= dismissedAt + 5;
      });
      setActiveStorms(visible);
      // Auto-switch away from tormentas when all storms clear
      if (visible.length === 0 && activeTab === 'tormentas') {
        setActiveTab('alarmas');
      }
    }
    recompute();
    const timer = setInterval(recompute, 30_000);
    return () => clearInterval(timer);
  }, [events, activeTab]);

  /** Filter events by active tab + parent filters */

  const filteredCategories = activeTab === 'alarmas' ? ALARM_CATEGORIES : SYSTEM_CATEGORIES;
  const filteredEvents = events.filter(e => {
    if (!filteredCategories.includes(e.event_category)) return false;
    if (siteFilter && e.site_id !== siteFilter.siteId) return false;
    if (severityFilter !== 'ALL' && e.severity !== severityFilter) return false;
    if (acknowledgedFilter === 'PENDING' && e.acknowledged_at) return false;
    if (acknowledgedFilter === 'ACKNOWLEDGED' && !e.acknowledged_at) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        e.cid_code.includes(search) ||
        e.zone_name?.toLowerCase().includes(q) ||
        e.site_name?.toLowerCase().includes(q) ||
        e.panel_serial?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Apply user-selected column sort (FIFO ASC by default)
  const sortedEvents = sortEvents(filteredEvents, sortField, sortDir);

  // Notify parent of the filtered set so it can drive context-aware controls
  // (Procesar lote counter, header badge, etc.). Use a cheap signature that
  // captures length + ack-count + endpoints to avoid stringifying thousands of ids.
  const ackedCount = filteredEvents.reduce((n, e) => n + (e.acknowledged_at ? 1 : 0), 0);
  const filteredSignature = `${filteredEvents.length}:${ackedCount}:${filteredEvents[0]?.id ?? ''}:${filteredEvents[filteredEvents.length - 1]?.id ?? ''}`;
  useEffect(() => {
    onFilteredChange?.(filteredEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSignature]);

  // Pagination derived state
  const totalRows = sortedEvents.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pageStart = safePage * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, totalRows);
  const pagedEvents = sortedEvents.slice(pageStart, pageEnd);

  // Reset to first page whenever filters / sort / tab change so the operator
  // never lands on an empty page after a filter narrows the result set.
  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab, severityFilter, acknowledgedFilter, search, siteFilter, sortField, sortDir, pageSize]);

  // Click handler for sortable column headers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // First click on a new column: ascending (FIFO for time, A→Z for text)
      setSortDir('asc');
    }
  };

  // Load initial events with site name
  useEffect(() => {
    async function loadEvents() {
      // Chunked fetch in pages of 1000 to bypass PostgREST max_rows cap
      const PAGE_SIZE = 1000;
      const collected: any[] = [];
      let from = 0;
      let lastError: any = null;
      while (collected.length < maxEvents) {
        const to = Math.min(from + PAGE_SIZE, maxEvents) - 1;
        let query = supabase
          .from('arc_events')
          .select(`
            *,
            alarm_sites ( name ),
            alarm_panels ( serial_number )
          `)
          .order('received_at', { ascending: false })
          .range(from, to);

        // Respect the acknowledgedFilter prop so the initial load matches what's visible
        if (acknowledgedFilter === 'PENDING') {
          query = query.is('acknowledged_at', null);
        } else if (acknowledgedFilter === 'ACKNOWLEDGED') {
          query = query.not('acknowledged_at', 'is', null);
        }

        const { data, error } = await query;
        if (error) { lastError = error; break; }
        const batch = data ?? [];
        collected.push(...batch);
        if (batch.length < to - from + 1) break; // exhausted
        from = to + 1;
      }

      if (lastError) {
        console.error('[Ribbon] Query error:', lastError);
        return;
      }

      const data = collected;
      if (data.length > 0) {
        // Transform to include site_name
        const transformed = data.map(e => ({
          ...e,
          site_name: (e.alarm_sites as { name?: string })?.name,
          panel_serial: (e.alarm_panels as { serial_number?: string })?.serial_number,
        }));
        setEvents(transformed);

        // Populate module-level caches from the initial joined load.
        // Without this, the storeEvents effect fires on every WS update and
        // launches individual Supabase calls for every uncached site/panel
        // → hundreds of concurrent requests → ERR_INSUFFICIENT_RESOURCES.
        data.forEach(e => {
          const siteName = (e.alarm_sites as { name?: string })?.name;
          if (e.site_id && siteName) siteNameCache.set(e.site_id, siteName);
          const serial = (e.alarm_panels as { serial_number?: string })?.serial_number;
          if (e.panel_id && serial) panelSerialCache.set(e.panel_id, serial);
        });

        // Pre-load schedules for all distinct sites present in the initial load
        const siteIds = [...new Set(data.map(e => e.site_id).filter(Boolean) as string[])];
        const toFetch = siteIds.filter(id => !scheduleCache.current.has(id) && !scheduleFetching.current.has(id));
        toFetch.forEach(id => scheduleFetching.current.add(id));
        Promise.all(
          toFetch.map(id =>
            arcService.fetchSiteSchedule(id)
              .then(s => { scheduleCache.current.set(id, s); })
              .finally(() => { scheduleFetching.current.delete(id); })
          )
        ).then(() => { if (toFetch.length > 0) setScheduleVersion(v => v + 1); })
          .catch(() => { /* non-critical */ });
      }
    }
    loadEvents();
  }, [maxEvents, acknowledgedFilter, refreshTrigger]);

  // Subscribe to realtime inserts
  useEffect(() => {
    const channel = supabase
      .channel('arc-events-ribbon')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'arc_events' },
        (payload) => {
          setEvents((prev) =>
            prev.map((e) =>
              e.id === (payload.new as ArcEventWithSite).id
                ? { ...e, ...(payload.new as ArcEventWithSite) }
                : e
            )
          );
        }
      )
      .on<ArcEventWithSite>(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'arc_events',
        },
        async (payload) => {
          // Spread to avoid mutating the Realtime payload object
          const newEvent = { ...(payload.new as ArcEventWithSite) };

          if (newEvent.site_id) {
            if (siteNameCache.has(newEvent.site_id)) {
              newEvent.site_name = siteNameCache.get(newEvent.site_id);
            } else {
              const { data: site } = await supabase.from('alarm_sites').select('name').eq('id', newEvent.site_id).single();
              if (site?.name) { siteNameCache.set(newEvent.site_id, site.name); newEvent.site_name = site.name; }
            }
          }

          if (newEvent.panel_id) {
            if (panelSerialCache.has(newEvent.panel_id)) {
              newEvent.panel_serial = panelSerialCache.get(newEvent.panel_id);
            } else {
              const { data: panel } = await supabase.from('alarm_panels').select('serial_number').eq('id', newEvent.panel_id).single();
              if (panel?.serial_number) { panelSerialCache.set(newEvent.panel_id, panel.serial_number); newEvent.panel_serial = panel.serial_number; }
            }
          }
          
          setEvents((prev) => {
            // Skip if already added by the store-sync (WebSocket arrived first)
            if (prev.some((e) => e.id === newEvent.id)) return prev;
            const merged = sortByTime([newEvent, ...prev]).slice(0, maxEvents);
            return merged;
          });

          // Auto-scroll to top if enabled
          if (autoScroll && containerRef.current) {
            containerRef.current.scrollTop = 0;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [maxEvents, autoScroll]);

  if (events.length === 0) {
    return (
      <div className={cn(
        'flex items-center justify-center p-8 text-muted-foreground',
        'border rounded-lg bg-muted/30',
        className
      )}>
        <div className="text-center">
          <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Sin eventos recientes</p>
          <p className="text-xs opacity-70 mt-1">Los eventos aparecerán aquí en tiempo real</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Header with Tabs */}
      <div className="flex items-center justify-between mb-3 px-1 flex-shrink-0">
        <div className="flex items-center gap-4">
          {/* Tabs */}
          <div className="flex rounded-lg bg-muted p-1 gap-1">
            <button
              onClick={() => setActiveTab('alarmas')}
              className={cn(
                'px-3 py-1 text-sm rounded-md transition-colors',
                activeTab === 'alarmas' 
                  ? 'bg-background text-foreground shadow-sm font-medium' 
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <AlertTriangle className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5" />
              Alarmas
              <span className="ml-1.5 text-xs opacity-70">
                ({events.filter(e =>
                  ALARM_CATEGORIES.includes(e.event_category) &&
                  (!siteFilter || e.site_id === siteFilter.siteId) &&
                  (acknowledgedFilter !== 'PENDING' || !e.acknowledged_at) &&
                  (acknowledgedFilter !== 'ACKNOWLEDGED' || e.acknowledged_at)
                ).length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab('sistema')}
              className={cn(
                'px-3 py-1 text-sm rounded-md transition-colors',
                activeTab === 'sistema' 
                  ? 'bg-background text-foreground shadow-sm font-medium' 
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <Activity className="h-3.5 w-3.5 inline-block mr-1.5 -mt-0.5" />
              Sistema
              <span className="ml-1.5 text-xs opacity-70">
                ({events.filter(e =>
                  SYSTEM_CATEGORIES.includes(e.event_category) &&
                  (!siteFilter || e.site_id === siteFilter.siteId) &&
                  (acknowledgedFilter !== 'PENDING' || !e.acknowledged_at) &&
                  (acknowledgedFilter !== 'ACKNOWLEDGED' || e.acknowledged_at)
                ).length})
              </span>
            </button>
            {activeStorms.length > 0 && (
              <button
                onClick={() => setActiveTab('tormentas')}
                className={cn(
                  'px-3 py-1 text-sm rounded-md transition-colors flex items-center gap-1.5',
                  activeTab === 'tormentas'
                    ? 'bg-red-500/15 text-red-600 dark:text-red-400 shadow-sm font-medium'
                    : 'text-red-500 hover:bg-red-500/10'
                )}
              >
                <Zap className="h-3.5 w-3.5 animate-pulse" />
                Saturación
                <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {activeStorms.length}
                </span>
              </button>
            )}
          </div>
          
          {/* Live indicator */}
          {isLive && (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              EN VIVO
            </span>
          )}
          {/* Active site filter chip */}
          {siteFilter && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:text-blue-400">
              <Filter className="h-3 w-3 flex-shrink-0" />
              <span className="max-w-[160px] truncate font-medium">{siteFilter.siteName}</span>
              <button onClick={() => setSiteFilter(null)} title="Quitar filtro de cliente">
                <X className="h-3 w-3 hover:opacity-70 transition-opacity" />
              </button>
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {activeTab === 'tormentas'
            ? `${activeStorms.length} tormenta${activeStorms.length !== 1 ? 's' : ''} activa${activeStorms.length !== 1 ? 's' : ''}`
            : `${filteredEvents.length} evento${filteredEvents.length !== 1 ? 's' : ''}${siteFilter ? '' : ''}`
          }
          {siteFilter && activeTab !== 'tormentas' && <span className="ml-1 text-blue-500">(filtrado)</span>}
        </span>
      </div>



      {/* Table */}
      <div 
        ref={containerRef}
        className="flex-1 min-h-0 border rounded-lg overflow-hidden flex flex-col"
      >
        {/* Table Header — hidden for tormentas tab */}
        {activeTab !== 'tormentas' && (
        <div className="flex-shrink-0 grid grid-cols-[80px_65px_1fr_2fr_85px_40px_50px_170px] gap-2 px-3 py-2 bg-muted/50 border-b text-xs font-medium text-muted-foreground">
          <SortHeaderCell label="Fecha" field="received_at" align="center" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <SortHeaderCell label="Hora" field="received_at" align="center" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <SortHeaderCell label="Evento" field="event" align="left" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <SortHeaderCell label="Cliente" field="site_name" align="left" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <SortHeaderCell label="Categoría" field="event_category" align="center" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <SortHeaderCell label="Z" field="zone_no" align="center" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <SortHeaderCell label="Prio." field="priority" align="center" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
          <div className="flex items-center justify-center">Acciones</div>
        </div>
        )}

        {/* Table Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* ── Tormentas tab content ─────────────────────────── */}
          {activeTab === 'tormentas' ? (
            <div className="p-3 space-y-2">
              {activeStorms.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Sin tormentas activas</div>
              ) : activeStorms.map(storm => (
                <PanelStormBanner
                  key={storm.key}
                  storm={storm}
                  onDismiss={() => {
                    dismissedStormsRef.current.set(storm.key, storm.recentEvents.length);
                    setActiveStorms(prev => {
                      const next = prev.filter(s => s.key !== storm.key);
                      if (next.length === 0) setActiveTab('alarmas');
                      return next;
                    });
                  }}
                  onFilter={() => {
                    if (storm.siteId) setSiteFilter({ siteId: storm.siteId, siteName: storm.siteName });
                    const hasAlarms = storm.recentEvents.some(e => ALARM_CATEGORIES.includes(e.event_category));
                    setActiveTab(hasAlarms ? 'alarmas' : 'sistema');
                  }}
                />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              Sin eventos de {activeTab === 'alarmas' ? 'alarma' : 'sistema'}
            </div>
          ) : (
            pagedEvents.map((event, index) => {
            // Use received_at (gateway server time, always UTC) for display.
            // occurred_at comes from the panel clock which may report local time as UTC.
            const { date, time } = formatDateTime(event.received_at);
            const isAcked = !!event.acknowledged_at;
            // Delayed: panel was offline; event occurred long before we received it.
            const delayMs = new Date(event.received_at).getTime() - new Date(event.occurred_at).getTime();
            const isDelayed = delayMs > 5 * 60 * 1000;
            
            return (
              <div
                key={event.id}
                className={cn(
                  'group grid grid-cols-[80px_65px_1fr_2fr_85px_40px_50px_170px] gap-2 px-3 py-2 border-b last:border-b-0',
                  'hover:bg-muted/50 transition-colors cursor-pointer text-sm',
                  getSeverityRowClass(event.severity, isAcked),
                  index === 0 && 'animate-in slide-in-from-top-1 duration-300'
                )}
              >
                {/* Fecha */}
                <div className="flex items-center justify-center font-mono text-xs" title={isDelayed ? `Recibido: ${formatDateTime(event.received_at).time}` : undefined}>
                  {date}
                </div>
                
                {/* Hora */}
                <div className="flex items-center justify-center gap-1 font-mono text-xs font-medium" title={isDelayed ? `Recibido: ${formatDateTime(event.received_at).time}` : undefined}>
                  {time}
                  {isDelayed && <span title={`Evento atrasado — recibido ${Math.round(delayMs / 60000)} min después`} className="text-orange-500">⚡</span>}
                </div>
                
                {/* Evento */}
                <div className="flex items-center gap-2 min-w-0">
                  {getSeverityIcon(event.severity)}
                  <div className="flex items-center gap-1.5 min-w-0">
                    {/* Text block — truncates to make room for the badge */}
                    <div className="truncate">
                      <span className="font-medium">{getEventDisplayName(event)}</span>
                      {/* CID description subtitle for open_close events */}
                      {getOpenCloseType(event) && (
                        <span className="text-muted-foreground ml-1.5 text-xs">
                          {arcService.getCidDescription(event.cid_code)}
                        </span>
                      )}
                      <span className="text-muted-foreground ml-1.5 text-xs">
                        ({event.cid_code})
                      </span>
                    </div>
                    {/* Out-of-hours badge — flex-shrink-0 sibling so it's never clipped */}
                    {event.event_category === 'open_close' && event.site_id && (() => {
                      // scheduleVersion accessed here to force re-render when cache updates
                      void scheduleVersion;
                      const sched = scheduleCache.current.get(event.site_id);
                      if (!sched) return null;
                      const ooh = arcService.isOutOfHours(event.occurred_at, sched, 'America/Guatemala');
                      if (!ooh) return null;
                      return (
                        <span
                          title="Apertura/cierre fuera del horario configurado"
                          className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20"
                        >
                          <MoonStar className="h-2.5 w-2.5" />
                          Fuera de horario
                        </span>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Cliente */}
                <div className="flex items-center min-w-0">
                  <div className="truncate">
                    {event.site_name ? (
                      <button
                        onClick={(ev) => {
                          ev.stopPropagation();
                          if (!event.site_id) return;
                          setSiteFilter(siteFilter?.siteId === event.site_id
                            ? null  // toggle off if already filtering this site
                            : { siteId: event.site_id, siteName: event.site_name! });
                        }}
                        title={siteFilter?.siteId === event.site_id ? 'Quitar filtro' : `Filtrar: ${event.site_name}`}
                        className={cn(
                          'font-semibold text-left transition-colors',
                          siteFilter?.siteId === event.site_id
                            ? 'text-blue-500'
                            : 'text-foreground hover:text-blue-500'
                        )}
                      >
                        {event.site_name}
                      </button>
                    ) : (
                      <span className="text-muted-foreground italic">Sin asignar</span>
                    )}
                    {event.panel_serial && (
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        [{event.panel_serial}]
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Categoría */}
                <div className="flex items-center justify-center gap-1 text-xs">
                  {getCategoryLabel(event)}
                  {event.event_category === 'open_close' && event.site_id && (() => {
                    void scheduleVersion;
                    const sched = scheduleCache.current.get(event.site_id);
                    if (!sched) return null;
                    const ooh = arcService.isOutOfHours(event.occurred_at, sched, 'America/Guatemala');
                    if (!ooh) return null;
                    return <MoonStar className="h-3 w-3 text-purple-500 flex-shrink-0" />;
                  })()}
                </div>
                
                {/* Zona */}
                <div className="flex items-center justify-center text-xs">
                  {event.zone_no != null ? (
                    <span title={event.zone_name || undefined}>
                      Z{event.zone_no + 1}
                    </span>
                  ) : '-'}
                </div>

                {/* Prioridad */}
                <div className="flex items-center justify-center">
                  <span
                    title={`Prioridad ${arcService.getEventPriority(event)}`}
                    className="text-xs font-mono tabular-nums text-muted-foreground"
                  >
                    {arcService.getEventPriority(event)}
                  </span>
                </div>
                
                {/* Acciones */}
                <div className="flex items-center justify-center gap-1.5">
                  {(() => {
                    if (!isAcked) {
                      // Count/collect ALL pending events for this site in the current tab,
                      // ignoring siteFilter / severityFilter / search so the button always
                      // reflects (and processes) the full backlog for the site — not just
                      // the subset visible under the active secondary filters.
                      const sitePending = onBatchBySite && event.site_id
                        ? events.filter(e =>
                            filteredCategories.includes(e.event_category) &&
                            e.site_id === event.site_id &&
                            !e.acknowledged_at
                          )
                        : [];
                      const pendingForSite = sitePending.length;
                      return (
                        <>
                          {onProcess && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onProcess(event); }}
                              className="px-2 py-1 text-xs font-medium rounded-md whitespace-nowrap bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                            >
                              Procesar
                            </button>
                          )}
                          {pendingForSite >= 2 && (
                            <button
                              onClick={(ev) => {
                                ev.stopPropagation();
                                onBatchBySite!(event.site_id!, event.site_name!, sitePending);
                              }}
                              title={`Procesar ${pendingForSite} eventos pendientes de ${event.site_name}`}
                              className="px-2 py-1 text-xs font-medium rounded-md whitespace-nowrap bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 transition-colors"
                            >
                              Lote ({pendingForSite})
                            </button>
                          )}
                        </>
                      );
                    }
                    if (event.resolved_at) {
                      return <CheckCircle className="h-4 w-4 text-green-500" />;
                    }
                    if (isAcked) {
                      return <CheckCircle className="h-4 w-4 text-blue-400 opacity-70" />;
                    }
                    return (
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })
          )}
        </div>

        {/* Pagination footer — only for alarmas/sistema tabs (tormentas has its own layout) */}
        {activeTab !== 'tormentas' && totalRows > 0 && (
          <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>Filas por página:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="bg-background border rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
              {sortField === 'priority' && sortDir === 'asc' && (
                <span
                  title="Ordenado por prioridad: Robo → Pánico → Médico → Incendio → … → Cierre"
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20 font-medium"
                >
                  <ShieldAlert className="h-3 w-3" />
                  Prioridad
                </span>
              )}
              {sortField === 'received_at' && sortDir === 'asc' && (
                <span
                  title="La cola sigue orden FIFO: la primera alarma en llegar se mantiene arriba hasta que se procese"
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 font-medium"
                >
                  <ArrowUp className="h-3 w-3" />
                  FIFO
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-medium text-foreground">
                {pageStart + 1}–{pageEnd} de {totalRows}
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={safePage === 0}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  title="Primera página"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  title="Página anterior"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 font-medium text-foreground min-w-[60px] text-center">
                  {safePage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={safePage >= totalPages - 1}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  title="Página siguiente"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={safePage >= totalPages - 1}
                  className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  title="Última página"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Sortable column header cell. Shows an arrow when the column is the active sort key. */
function SortHeaderCell({
  label,
  field,
  align,
  sortField,
  sortDir,
  onSort,
}: {
  label: string;
  field: SortField;
  align: 'left' | 'center';
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={cn(
        'flex items-center gap-1 select-none transition-colors hover:text-foreground',
        align === 'center' ? 'justify-center' : 'justify-start',
        active && 'text-foreground font-semibold'
      )}
      title={`Ordenar por ${label}`}
    >
      <span>{label}</span>
      {active && (
        sortDir === 'asc'
          ? <ArrowUp className="h-3 w-3" />
          : <ArrowDown className="h-3 w-3" />
      )}
    </button>
  );
}
